import React, { useState, useEffect } from "react";
import { MessageCircle, Star, Plus, Pencil, X, ChevronDown } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./CouriersListPage.css";
import AddCourierModal from "./AddCourierModal";
import CourierDetailsPanel from "../../../components/shared/CourierDetailsPanel/CourierDetailsPanel";
import axios from "axios";
import cacheStorage from '../../../utils/cacheStorage';

export default function CouriersListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchTerm = searchParams.get('search') || '';
  const [couriers, setCouriers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [editCourier, setEditCourier] = useState(null);
  const [blockCourier, setBlockCourier] = useState(null);
  const [blockedIds, setBlockedIds] = useState([]);
  const [editFormData, setEditFormData] = useState({ name: '', phone: '', vehicle: '', plateNo: '' });
  const [vehicles, setVehicles] = useState([]);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);
  const [unblockCourier, setUnblockCourier] = useState(null);

  const fetchCouriers = async () => {
    try {
      const token = cacheStorage.getItem('access_token');
      const response = await axios.get('https://smart-cargo-backend-923182991420.us-central1.run.app/api/admin/couriers', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = response.data || [];
      const mappedCouriers = data.map(c => ({
        id: `CUR-${String(c.id).padStart(3, '0')}`,
        dbId: c.id,
        auth0UserId: c.auth0_user_id,
        isBlocked: Boolean(c.is_blocked),
        name: c.name,
        phone: c.phone,
        deliveries: c.deliveries,
        rating: c.rating != null ? c.rating.toFixed(1) : "N/A",
        status: c.is_blocked ? "blocked" : "active",
        vehicle: c.vehicle,
        plateNo: c.plate_no || ''
      }));

      setCouriers(mappedCouriers);
      setBlockedIds(mappedCouriers.filter(c => c.isBlocked).map(c => c.dbId));
    } catch (error) {
      console.error('Failed to fetch couriers:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const token = cacheStorage.getItem('access_token');
      const response = await axios.get('https://smart-cargo-backend-923182991420.us-central1.run.app/api/vehicles', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = response.data || [];
      setVehicles(data.map(v => ({ id: v.id, plateNo: v.plate_no, model: v.model })));
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
    }
  };

  useEffect(() => {
    fetchCouriers();
    fetchVehicles();
  }, []);

  useEffect(() => {
    if (searchTerm && couriers.length > 0) {
      const match = couriers.find(c => c.id === searchTerm);
      if (match) {
        setSelectedCourier(match);
      }
    }
  }, [searchTerm, couriers]);

  const handleAddSuccess = async (newCourierData) => {
    const matched = vehicles.find(v => v.plateNo === newCourierData.plateNo);

    try {
      const token = cacheStorage.getItem('access_token');

      await axios.post('https://smart-cargo-backend-923182991420.us-central1.run.app/api/admin/couriers', {
        fullName: newCourierData.fullName,
        username: newCourierData.username,
        phone: newCourierData.phone,
        vehicleId: matched ? matched.id : null,
        password: newCourierData.password
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      await fetchCouriers();
    } catch (error) {
      console.error('Failed to add courier:', error);
      throw error;
    }
  };

  const handleEditClick = (e, courier) => {
    e.stopPropagation();
    const matched = vehicles.find(v => v.plateNo === courier.plateNo || v.plateNo === courier.vehicle);
    setEditFormData({
      name: courier.name,
      phone: courier.phone,
      plateNo: matched ? matched.plateNo : (courier.plateNo || ''),
      vehicle: matched ? matched.model : (courier.vehicle || '')
    });
    setVehicleSearch('');
    setVehicleDropdownOpen(false);
    setEditCourier(courier);
  };

  const handleEditSave = async () => {
    try {
      const token = cacheStorage.getItem('access_token');
      await axios.put(`https://smart-cargo-backend-923182991420.us-central1.run.app/api/admin/couriers/${editCourier.dbId}`, {
        name: editFormData.name,
        phone: editFormData.phone,
        vehicle: editFormData.vehicle,
        plate_no: editFormData.plateNo,
      }, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      await fetchCouriers();
    } catch (error) {
      console.error('Failed to update courier:', error);
    }
    setEditCourier(null);
  };

  const handleBlockConfirm = async () => {
    try {
      const token = cacheStorage.getItem('access_token');

      await axios.patch(
          `https://smart-cargo-backend-923182991420.us-central1.run.app/api/admin/couriers/${blockCourier.dbId}/block`,
          { blocked: true },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
      );

      await fetchCouriers();
    } catch (error) {
      console.error('Block request failed:', error);
    }

    setBlockCourier(null);
  };


  const handleUnblockConfirm = async () => {
    try {
      const token = cacheStorage.getItem('access_token');
      await axios.patch(
          `https://smart-cargo-backend-923182991420.us-central1.run.app/api/admin/couriers/${unblockCourier.dbId}/block`,
          { blocked: false },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
      );
      await fetchCouriers();
    } catch (error) {
      console.error('Unblock request failed:', error);
    }
    setUnblockCourier(null);
  };


  const filteredCouriers = couriers.filter(
      (courier) =>
          courier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          courier.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredVehicles = vehicles.filter(v =>
      v.plateNo.toLowerCase().includes(vehicleSearch.toLowerCase()) ||
      v.model.toLowerCase().includes(vehicleSearch.toLowerCase())
  );

  return (
      <div className="couriers-list-page">
        <div className="couriers-header-container">
          <div className="couriers-header-titles">
            <h1 className="couriers-title">Couriers</h1>
            <p className="couriers-subtitle">Manage courier fleet and performance</p>
          </div>

          <div className="couriers-header-actions">
            <button className="btn-secondary" onClick={() => navigate("/admin/couriers/performance")}>
              View Performance
            </button>
            <button className="btn-primary" onClick={() => setIsModalOpen(true)}>
              <Plus size={18} />
              Add Courier
            </button>
          </div>
        </div>

        <div className="table-container">
          <table className="couriers-table">
            <thead>
            <tr>
              <th>Courier ID</th>
              <th>Name</th>
              <th>Contact</th>
              <th>Deliveries</th>
              <th>Rating</th>
              <th>Vehicle</th>
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
            </thead>
            <tbody>
            {filteredCouriers.map((courier) => (
                <tr
                    key={courier.id}
                    onClick={() => setSelectedCourier(courier)}
                    style={{ cursor: "pointer" }}
                    className={blockedIds.includes(courier.dbId) ? "courier-row-blocked" : ""}
                >
                  <td className="col-id">{courier.id}</td>
                  <td className="col-name">
                    <div className="avatar-name">
                      <div className="avatar-circle">{courier.name.charAt(0)}</div>
                      <div className="name-text">{courier.name}</div>
                    </div>
                  </td>
                  <td className="col-contact">
                    <div className="contact-info">
                      <div className="phone">{courier.phone}</div>
                    </div>
                  </td>
                  <td className="col-deliveries">{courier.deliveries}</td>
                  <td className="col-rating">
                    <div className="rating-badge">
                      <Star size={16} className="star-icon" fill="currentColor" />
                      <span>{courier.rating}</span>
                    </div>
                  </td>
                  <td className="col-vehicle">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ color: '#1e3a8a', fontWeight: 500 }}>{courier.vehicle}</span>
                      <span style={{ color: '#9ca3af', fontSize: '12px' }}>{courier.plateNo || '—'}</span>
                    </div>
                  </td>
                  <td className="col-actions">
                    <div className="action-btns-group">
                      <button className="action-btn" title="Contact courier" onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/couriers/${courier.dbId}/contact`);
                      }}>
                        <MessageCircle size={20} />
                      </button>
                      <button className="action-btn action-btn-edit" title="Edit courier" onClick={(e) => handleEditClick(e, courier)}>
                        <Pencil size={18} />
                      </button>
                      <button
                          className={`action-btn action-btn-block${blockedIds.includes(courier.dbId) ? ' blocked' : ''}`}
                          title={blockedIds.includes(courier.dbId) ? "Unblock courier" : "Block courier"}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (blockedIds.includes(courier.dbId)) {
                              setUnblockCourier(courier);
                            } else {
                              setBlockCourier(courier);
                            }
                          }}
                          style={blockedIds.includes(courier.dbId) ? { color: '#16a34a', cursor: 'pointer', opacity: 1 } : {}}
                      >
                        {blockedIds.includes(courier.dbId) ? (
                            // Green tick — click to unblock
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/>
                              <polyline points="9 12 11 14 15 10"/>
                            </svg>
                        ) : (
                            // Red ban — click to block
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                            </svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
            ))}
            </tbody>
          </table>
        </div>

        <AddCourierModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onAddSuccess={handleAddSuccess}
            vehicles={vehicles}
        />
        <CourierDetailsPanel
            isOpen={!!selectedCourier}
            onClose={() => setSelectedCourier(null)}
            courierData={selectedCourier}
            onEdit={(data) => {
              setSelectedCourier(null);
              const matched = vehicles.find(v => v.plateNo === data.plateNo || v.plateNo === data.vehicle);
              setEditFormData({
                name: data.name,
                phone: data.phone,
                plateNo: matched ? matched.plateNo : (data.plateNo || ''),
                vehicle: matched ? matched.model : (data.vehicle || '')
              });
              setVehicleSearch('');
              setVehicleDropdownOpen(false);
              setEditCourier(data);
            }}
        />

        {/* Edit Courier Modal */}
        {editCourier && (
            <div className="modal-overlay" onClick={() => setEditCourier(null)}>
              <div className="modal-content edit-courier-modal" onClick={e => { e.stopPropagation(); setVehicleDropdownOpen(false); }}>
                <div className="modal-header">
                  <div className="modal-title-group">
                    <h2>Edit Courier</h2>
                    <p className="modal-subtitle">Update courier details</p>
                  </div>
                  <button className="modal-close" onClick={() => setEditCourier(null)}>
                    <X size={20} />
                  </button>
                </div>
                <div className="modal-form">
                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                        value={editFormData.name}
                        onChange={e => setEditFormData(p => ({ ...p, name: e.target.value }))}
                        placeholder="Full name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                        value={editFormData.phone}
                        onChange={e => setEditFormData(p => ({ ...p, phone: e.target.value }))}
                        placeholder="Phone number"
                    />
                  </div>

                  {/* Plate No — combobox: trigger becomes the search input when open */}
                  <div className="form-group">
                    <label>Vehicle Plate Number</label>
                    <div style={{ position: 'relative' }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        border: '1px solid #d1d5db',
                        borderRadius: vehicleDropdownOpen ? '8px 8px 0 0' : '8px',
                        padding: '10px 14px', background: '#fff', fontSize: '14px',
                        transition: 'border-radius 0.15s'
                      }}>
                        {vehicleDropdownOpen ? (
                            <input
                                autoFocus
                                value={vehicleSearch}
                                onChange={e => setVehicleSearch(e.target.value)}
                                onClick={e => e.stopPropagation()}
                                placeholder="Search plate or model..."
                                style={{
                                  border: 'none', outline: 'none', boxShadow: 'none', width: '100%',
                                  fontSize: '14px', background: 'transparent', color: '#111827'
                                }}
                            />
                        ) : (
                            <span
                                onClick={(e) => { e.stopPropagation(); setVehicleDropdownOpen(true); }}
                                style={{ flex: 1, cursor: 'pointer', color: editFormData.plateNo ? '#111827' : '#9ca3af' }}
                            >
                            {editFormData.plateNo || 'Select plate number...'}
                          </span>
                        )}
                        <ChevronDown
                            size={16}
                            onClick={(e) => { e.stopPropagation(); setVehicleDropdownOpen(o => !o); }}
                            style={{
                              color: '#6b7280', flexShrink: 0, cursor: 'pointer',
                              transform: vehicleDropdownOpen ? 'rotate(180deg)' : 'none',
                              transition: 'transform 0.2s'
                            }}
                        />
                      </div>

                      {vehicleDropdownOpen && (
                          <div
                              style={{
                                position: 'absolute', left: 0, right: 0,
                                background: '#fff',
                                border: '1px solid #d1d5db', borderTop: 'none',
                                borderRadius: '0 0 8px 8px',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 1000, overflow: 'hidden'
                              }}
                              onClick={e => e.stopPropagation()}
                          >
                            <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                              {filteredVehicles.length > 0 ? filteredVehicles.map(v => (
                                  <div
                                      key={v.plateNo}
                                      onClick={() => {
                                        setEditFormData(p => ({ ...p, plateNo: v.plateNo, vehicle: v.model }));
                                        setVehicleDropdownOpen(false);
                                        setVehicleSearch('');
                                      }}
                                      style={{
                                        padding: '10px 14px', cursor: 'pointer', fontSize: '13px',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        background: editFormData.plateNo === v.plateNo ? '#eff6ff' : 'transparent',
                                        color: '#111827', borderBottom: '1px solid #f5f5f5'
                                      }}
                                      onMouseEnter={e => { if (editFormData.plateNo !== v.plateNo) e.currentTarget.style.background = '#f9fafb'; }}
                                      onMouseLeave={e => { if (editFormData.plateNo !== v.plateNo) e.currentTarget.style.background = 'transparent'; }}
                                  >
                                    <span style={{ fontWeight: 500 }}>{v.plateNo}</span>
                                    <span style={{ color: '#6b7280', fontSize: '12px' }}>{v.model}</span>
                                  </div>
                              )) : (
                                  <div style={{ padding: '12px 14px', color: '#9ca3af', fontSize: '13px', textAlign: 'center' }}>No vehicles found</div>
                              )}
                            </div>
                          </div>
                      )}
                    </div>
                  </div>

                  {/* Vehicle — locked, auto-filled from selected plate */}
                  <div className="form-group">
                    <label style={{ color: '#9ca3af' }}>
                      Vehicle <span style={{ fontSize: '11px', fontWeight: 400 }}>(auto-filled)</span>
                    </label>
                    <input
                        value={editFormData.vehicle}
                        readOnly
                        placeholder="Auto-filled from plate number"
                        style={{ background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed', border: '1px solid #e5e7eb' }}
                    />
                  </div>

                  <div className="modal-actions">
                    <button className="btn-submit create-courier-btn" onClick={handleEditSave}>Save Changes</button>
                  </div>
                </div>
              </div>
            </div>
        )}

        {/* Block Confirmation Modal */}
        {blockCourier && (
            <div className="modal-overlay" onClick={() => setBlockCourier(null)}>
              <div className="modal-content block-confirm-modal" onClick={e => e.stopPropagation()}>
                <div className="block-confirm-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                  </svg>
                </div>
                <h2 className="block-confirm-title">ARE YOU SURE?</h2>
                <p className="block-confirm-subtitle">
                  You are about to block <strong>{blockCourier.name}</strong>. They will lose access to the system.
                </p>
                <div className="block-confirm-actions">
                  <button className="btn-cancel-block" onClick={() => setBlockCourier(null)}>Cancel</button>
                  <button className="btn-confirm-block" onClick={handleBlockConfirm}>Block This User</button>
                </div>
              </div>
            </div>
        )}

        {unblockCourier && (
            <div className="modal-overlay" onClick={() => setUnblockCourier(null)}>
              <div className="modal-content block-confirm-modal" onClick={e => e.stopPropagation()}>
                <div className="block-confirm-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="9 12 11 14 15 10"/>
                  </svg>
                </div>
                <h2 className="block-confirm-title">UNBLOCK COURIER?</h2>
                <p className="block-confirm-subtitle">
                  <strong>{unblockCourier.name}</strong> will regain access to the system.
                </p>
                <div className="block-confirm-actions">
                  <button className="btn-cancel-block" onClick={() => setUnblockCourier(null)}>Cancel</button>
                  <button
                      className="btn-confirm-block"
                      style={{ background: '#16a34a' }}
                      onClick={handleUnblockConfirm}
                  >
                    Unblock This User
                  </button>
                </div>
              </div>
            </div>
        )}

      </div>
  );
}